import axios from './axios';
import { isEmpty } from 'lodash';

import { HIGHLIGHT_ERROR, HIGHLIGHT_NORMAL, HIGHLIGHT_SUCCESS } from '../constants/exercise';
import { CORRECT, WRONG, MISSING, INCOMPLETE } from './exercise';

import { CHECKER_URL } from '../config';

const ANSWER_TYPES = {
    STEP: 'step',
    RESULT: 'result',
};

export const VALIDATION_STATUSES = {
    VALID: 'VALID',
    ERROR: 'ERROR',
    MISSING: 'MISSING',
    PENDING: 'PENDING',
};

export const RESULTS = {
    IMPOSSIBLE: '<math xmlns="http://www.w3.org/1998/Math/MathML"><mo>&#8708;</mo></math>',
    INDETERMINATE: '<math xmlns="http://www.w3.org/1998/Math/MathML"><mo>&#8704;</mo></math>',
};

const VALIDATION_MAP = {
    true: VALIDATION_STATUSES.ERROR,
    false: VALIDATION_STATUSES.VALID,
};

export const AND = '<math xmlns="http://www.w3.org/1998/Math/MathML"><mo>&#x2227;</mo></math>';

export const MATH_XMLNS = 'http://www.w3.org/1998/Math/MathML';

export const FINAL_ANSWER_RESULTS = {
    STEP: 'step',
    IMPOSSIBLE: 'impossible',
    INDETERMINATE: 'indeterminate',
};

export const ANSWER_LABELS = {
    [FINAL_ANSWER_RESULTS.STEP]: "Sì, l'ultimo passaggio è il risultato",
    [FINAL_ANSWER_RESULTS.IMPOSSIBLE]: "Sì, l'espressione è impossibile",
    [FINAL_ANSWER_RESULTS.INDETERMINATE]: "Sì, l'espressione è indeterminata",
};

export const getAnswerType = (exercise) => {
    const answerType = exercise.json.answerType;

    switch (answerType) {
        case null:
            return FINAL_ANSWER_RESULTS.STEP;
        case FINAL_ANSWER_RESULTS.INDETERMINATE:
        case FINAL_ANSWER_RESULTS.IMPOSSIBLE:
        default:
            return answerType;
    }
};

export const getAnswerContent = (exercise) => {
    const answerContent = exercise.json.answerContent;
    const answerType = exercise.json.answerType;

    switch (answerType) {
        case FINAL_ANSWER_RESULTS.INDETERMINATE:
            return RESULTS.INDETERMINATE;
        case FINAL_ANSWER_RESULTS.IMPOSSIBLE:
            return RESULTS.IMPOSSIBLE;
        default:
            return answerContent;
    }
};

export const getMathNode = (xmlString = '') => {
    const document = new DOMParser().parseFromString(xmlString, 'text/html');
    return document.querySelector('math');
};

export const getMathContent = (content) => {
    return getMathNode(content)?.innerHTML;
};

export const getCheckerQuestion = (question) => {
    const mathNode = getMathNode(question);
    if (mathNode) {
        return mathNode.outerHTML;
    }

    return '';
};

export const getCorrectionStatus = (correctionStatus) => {
    if (correctionStatus?.status) {
        return correctionStatus.status;
    }
    return correctionStatus.state;
};

export const getCheckboxHighlight = (checkboxType, correction, correctAnswerType, finalAnswerType) => {
    if (!correction) {
        return HIGHLIGHT_NORMAL;
    }

    const correctionState = getCorrectionStatus(correction);

    if (checkboxType === correctAnswerType) {
        /**
         * When:
         *   - Checkbox type is 'step'
         *   - User selected 'step' as final answer
         *
         * We should also check if the last inserted step is correct.
         */
        if (checkboxType === FINAL_ANSWER_RESULTS.STEP && finalAnswerType === FINAL_ANSWER_RESULTS.STEP) {
            if (correctionState !== CORRECT) {
                return HIGHLIGHT_ERROR;
            }
        }

        return HIGHLIGHT_SUCCESS;
    }

    const isChecked = finalAnswerType === checkboxType;

    return isChecked ? HIGHLIGHT_ERROR : HIGHLIGHT_NORMAL;
};

export const getCheckboxCorrection = (finalAnswerType, correctAnswerType, correctionStatusState) => {
    if (!finalAnswerType) {
        return MISSING;
    }

    if (finalAnswerType === correctAnswerType) {
        if (finalAnswerType === ANSWER_TYPES.STEP) {
            if (correctionStatusState !== CORRECT) {
                return WRONG;
            }
        }

        return CORRECT;
    }

    return WRONG;
};

export const isCheckerCorrectable = (answer = {}) => {
    return answer.finalAnswer;
};

const isCdesMissing = (cdes = []) => {
    return cdes.every((cde) => !getMathContent(cde.value));
};

export const isCdeNotEmpty = (cde) => {
    return !!getMathContent(cde.value);
};

export const getExecutionCorrection = (steps = []) => {
    if (isEmpty(steps)) {
        return MISSING;
    }

    const isExecutionError = steps.some((step) => step.validationStatus === VALIDATION_STATUSES.ERROR);

    if (isExecutionError) {
        return WRONG;
    }

    return CORRECT;
};

export const getCdesCorrection = (cdes = []) => {
    if (isEmpty(cdes) || isCdesMissing(cdes)) {
        return MISSING;
    }

    const isCdesCorrect = cdes.some((cde) => cde.validationStatus === VALIDATION_STATUSES.VALID);

    if (isCdesCorrect) {
        return CORRECT;
    }

    return WRONG;
};

export const getFinalAnswerCorrection = (exercise, finalAnswer) => {
    if (!finalAnswer) {
        return MISSING;
    }

    if (exercise.answerType !== finalAnswer) {
        return WRONG;
    }

    return CORRECT;
};

export const getLastStep = (finalAnswer, steps) => {
    const getStep = (value) => ({ value });

    switch (finalAnswer) {
        case FINAL_ANSWER_RESULTS.IMPOSSIBLE:
            return getStep(RESULTS.IMPOSSIBLE);
        case FINAL_ANSWER_RESULTS.INDETERMINATE:
            return getStep(RESULTS.INDETERMINATE);
        default:
            return steps[steps.length - 1];
    }
};

export const getCheckerCorrection = async (exercise = {}, answers = {}) => {
    const { steps, cdes, finalAnswer } = answers;

    const lastStep = getLastStep(finalAnswer, steps);

    const execution = [...steps, lastStep];
    const checkedExecution = await validateCheckerExecution(exercise, execution, ANSWER_TYPES.RESULT);
    const checkedCdes = await validateCheckerExistenceConditions(exercise, cdes);

    /**
     * Checker error codes (non-exaustive):
     *   - 007 steps are correct but some are missing
     *   - 010 steps correction skipped
     */
    const isIncomplete = checkedExecution.some((checkerError) => {
        const [errorCode] = checkerError.errorMessage.split('|');
        return ['007'].includes(errorCode);
    });

    const isCdeError = checkedCdes.some((cde) => cde.errorMessage);

    let isExecutionError = checkedExecution.some((checkerError) => {
        const [errorCode] = checkerError.errorMessage.split('|');
        return !['', '007', '010'].includes(errorCode);
    });

    if (exercise.answerType === FINAL_ANSWER_RESULTS.IMPOSSIBLE && finalAnswer === exercise.answerType) {
        isExecutionError = false;
    }

    if (!finalAnswer) {
        return MISSING;
    }

    if (isIncomplete) {
        return INCOMPLETE;
    }

    if (isCdeError || isExecutionError) {
        return WRONG;
    }

    return CORRECT;
};

export const getValidatedCheckerCdes = async (exercise = {}, cdes = []) => {
    const checkedCdes = await validateCheckerExistenceConditions(exercise, cdes);

    const isCdeError = checkedCdes.some((cde) => cde.errorMessage);

    const validatedCdes = cdes.map((cde) => ({
        ...cde,
        validationStatus: VALIDATION_MAP[!!isCdeError],
    }));

    return validatedCdes;
};

export const getValidatedCheckerSteps = async (exercise = {}, steps = []) => {
    const checkedSteps = await validateCheckerExecution(exercise, steps, ANSWER_TYPES.STEP);

    const validatedSteps = checkedSteps.map((validatedStep, index) => ({
        ...steps[index],
        validationStatus: VALIDATION_MAP[!!validatedStep.errorMessage],
    }));

    const skipValidation = exercise.answerType !== FINAL_ANSWER_RESULTS.STEP;
    if (skipValidation) {
        return steps.map((step) => ({ ...step, validationStatus: VALIDATION_STATUSES.VALID }));
    }

    return validatedSteps;
};

const validateCheckerExecution = async (exercise = {}, answers = [], type) => {
    const defaultMath = getCheckerQuestion(exercise.question);

    const answersMath = answers.map((answer) => answer.value);

    return await validateChecker(exercise.assertion, exercise.answerContent, [defaultMath, ...answersMath], type);
};

const validateCheckerExistenceConditions = async (exercise, cdes = []) => {
    if (exercise.existenceConditions.length === 0) {
        return [];
    }

    const { data } = await axios.request({
        method: 'POST',
        url: CHECKER_URL,
        data: {
            domain: {
                correct_domain: {
                    isolated_points: {
                        intersection_type: 'pos',
                        points: exercise.existenceConditions,
                    },
                },
                student_domain: {
                    isolated_points: {
                        intersection_type: 'pos',
                        points: cdes.map((cde) => cde.value),
                    },
                },
            },
        },
    });

    return data.domain_results;
};

const validateChecker = async (assertionType, correctResult, listSteps, type, checkFirst = true, checkPrevious = true) => {
    const { data } = await axios.request({
        method: 'POST',
        url: CHECKER_URL,
        data: {
            assertion_type: assertionType,
            check_first: checkFirst,
            check_previous: checkPrevious,
            list_steps: listSteps,
            correct_result: correctResult,
            button: type,
        },
    });

    return data.checker_results;
};

export const isExerciseWithCdes = (exercise) => {
    return exercise.existenceConditions.length > 0;
};
